import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { WizardStepProps } from '@components/Wizard/Wizard';
import PrimaryButton from '@components/PrimaryButton/PrimaryButton';
import RecoveryPhrase from '@components/RecoveryPhrase/RecoveryPhrase';
import Checkbox from '@components/Checkbox/Checkbox';
import CircleBackground from '@assets/images/CircleBackground.png';
import { GIVONIC, INTER } from '@styles/fonts';
import { BLACK, BLUE, GRAYSCALE_100, GRAYSCALE_300, GRAYSCALE_500, DARK_VIOLET, WHITE } from '@styles/colors';
import { TonWalletUtils } from '@lib/secrets';
import LoadingModal from '@components/LoadingModal/LoadingModal';
import { useTranslation } from 'react-i18next';
import * as Sentry from "@sentry/react";
import Snackbar from '@components/Snackbar/Snackbar';
import { safeAreaInset } from '@lib/telegram';

// Styled components
const NotesContainer = styled.div`
    padding: 0 4vw 24px 4vw;
    display: flex;
    flex-direction: column;
    height: 100%;
    max-height: 100vh;
    overflow: hidden; /* Prevent any overflow from children */
`

const BlueText = styled.p`
    text-transform: uppercase;
    font-weight: 400;
    font-family: ${INTER.medium};
    font-size: 12px;
    line-height: 12px;
    text-align: center;
    color: ${BLUE};
    margin: 2vh 0px 0px 0px;
    text-transform: uppercase;
`

const Title = styled.p`
    font-family: ${GIVONIC.bold};
    font-weight: 600;
    font-size: 13px;
    line-height: 13px;
    text-align: center;
    color: ${BLACK};
    margin: 2vh 0;
    text-transform: capitalize;
`

const SubTitle = styled.p`
    font-family: ${INTER.thin};
    font-weight: 100;
    font-size: 13px;
    line-height: 13px;
    letter-spacing: 0.0012px;
    text-align: center;
    margin: 0 0 0 0;
    color: ${BLACK};
`

const FootNote = styled.p`
    font-family: ${INTER.light};
    font-weight: 300;
    font-size: 13px;
    line-height: 17px;
    letter-spacing: 0.0012px;
    text-align: center;
    color: ${BLACK};
    margin-vertical: 20px;
`

const CheckboxContainer = styled.div`
    margin-bottom: 8px;
    display: flex;
    align-items: center;
`

const NoteCard = styled.div`
    display: flex;
    flex-direction: row;
    gap: 1vw;
    width: 100%;
    background-color: #FAFDFF;
    border-radius: 12px;
    border: 1px solid #b2e2fb;
    padding: 2vh 5vw;
    box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.05);
`

const NoteCardContent = styled.div`
    display: flex;
    flex-direction: column;
`

const NoteNumber = styled.span`
    font-family: ${GIVONIC.bold};
    font-weight: 600;
    font-size: 13px;
    color: ${BLACK};
`

const NoteTitle = styled.p`
    font-family: ${GIVONIC.bold};
    font-weight: 600;
    font-size: 13px;
    color: ${BLACK};
    margin: 0;
    margin-bottom: 8px;
`

const NoteText = styled.p`
    font-family: ${INTER.light};
    font-weight: 300;
    font-size: 12px;
    line-height: 18px;
    color: ${DARK_VIOLET};
    margin: 0;
`

const ScrollableCardsContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    overflow-y: auto;
    flex: 1; /* Take all available space between top and bottom containers */
    min-height: 0; /* This is crucial for flex items to be able to shrink below their content size */
    gap: 10px;
    margin: 2vh 0;
    
    /* Hide scrollbar for Chrome, Safari and Opera */
    &::-webkit-scrollbar {
        width: 4px;
    }
    
    /* Track */
    &::-webkit-scrollbar-track {
        background: ${GRAYSCALE_100};
        border-radius: 10px;
    }
    
    /* Handle */
    &::-webkit-scrollbar-thumb {
        background: ${GRAYSCALE_300};
        border-radius: 10px;
    }
    
    /* Handle on hover */
    &::-webkit-scrollbar-thumb:hover {
        background: ${GRAYSCALE_500};
    }
`

const FixedBottomContainer = styled.div`
    margin-top: auto; /* Push to bottom */
    padding-bottom: ${safeAreaInset().bottom === 0 ? 24 : safeAreaInset().bottom - 24}px;
`

const FixedTopContainer = styled.div`
    margin-bottom: 0; /* Ensure it stays at the top */
`

const RecoveryPhraseContainer = styled.div`
    display: flex;
    overflow-y: auto;
    flex-direction: column;
    padding: 0 4vw 15vh 4vw;
    height: 100%;
    background-image: url(${CircleBackground});
    background-size: contain;
    background-position: right;
    background-repeat: no-repeat;
`

const RecoveryPhraseTitle = styled.p`
    font-family: ${GIVONIC.semiBold};
    font-weight: 500;
    font-size: 13px;
    line-height: 13px;
    color: ${BLACK};
    margin: 2vh 0;
    text-transform: capitalize;
`

const RecoveryDescription = styled.p`
    font-size: 13px;
    line-height: 18px;
    color: #333;
    margin: 3vh auto 1vh auto;
`

const ButtonWrapper = styled.div`
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    padding: 16px 12px 24px 12px;
    background-color: ${WHITE};
    padding-bottom: ${safeAreaInset().bottom || 24}px;
`

interface NoteCardProps {
    number: number;
    title: string;
    description: string;
}

const SecurityNoteCard: React.FC<NoteCardProps> = ({ number, title, description }) => {
    return (
        <NoteCard>
            <NoteNumber>{number}. </NoteNumber>
            <NoteCardContent>
                <NoteTitle>
                    {title}
                </NoteTitle>
                <NoteText>{description}</NoteText>
            </NoteCardContent>
        </NoteCard>
    );
};

const ImportantNotes = ({ onContinue }: { onContinue: () => void }) => {
    const [isAgreed, setIsAgreed] = useState(false);
    const { t } = useTranslation();

    const handleCheckboxChange = () => {
        setIsAgreed(!isAgreed);
    };

    // Example notes data - you can replace this with your actual data
    const securityNotes = t('page.createWallet.steps.recoveryPhrase.securityNotes', { returnObjects: true }) as Array<{ title: string, description: string }>;
    const notes = securityNotes.map((note, index) => ({
        number: index + 1,
        title: note.title,
        description: note.description
    }));

    return (
        <NotesContainer>
            <FixedTopContainer>
                <BlueText>{t('page.createWallet.steps.recoveryPhrase.important')}</BlueText>
                <Title>{t('page.createWallet.steps.recoveryPhrase.safeguardTitle')}</Title>
                <SubTitle>{t('page.createWallet.steps.recoveryPhrase.safeguardSubtitle')}</SubTitle>
            </FixedTopContainer>

            <ScrollableCardsContainer>
                {notes.map((note) => (
                    <SecurityNoteCard
                        key={note.number}
                        number={note.number}
                        title={note.title}
                        description={note.description}
                    />
                ))}
            </ScrollableCardsContainer>

            <FixedBottomContainer>
                <FootNote>{t('page.createWallet.steps.recoveryPhrase.footNote')}</FootNote>
                
                <CheckboxContainer>
                    <Checkbox 
                        checked={isAgreed} 
                        onChange={handleCheckboxChange} 
                        label={t('page.createWallet.steps.recoveryPhrase.checkbox')}
                    />
                </CheckboxContainer>
                
                <PrimaryButton onClick={onContinue} disabled={!isAgreed}>{t('page.createWallet.steps.recoveryPhrase.continue')}</PrimaryButton>
            </FixedBottomContainer>
        </NotesContainer>
    );
};

interface RecoveryPhraseDisplayProps extends WizardStepProps {
    walletMnemonic: string;
}

const RecoveryPhraseDisplay: React.FC<RecoveryPhraseDisplayProps> = ({ onNext, walletMnemonic }) => {
    const [phrases, setPhrases] = useState<string[]>(['', '', '', '', '', '', '', '', '', '', '', '']);
    const [isLoading, setIsLoading] = useState(true);
    const { t } = useTranslation();
    const [showSnackbar, setShowSnackbar] = useState(false)

    useEffect(() => {
        const loadWallet = async () => {
            setIsLoading(true)
            try {
                // Use the mnemonic passed from parent instead of retrieving from storage
                if (!walletMnemonic) {
                    throw new Error('Wallet mnemonic not available');
                }
                setPhrases(walletMnemonic.split(' '));
            } catch (error) {
                await TonWalletUtils.clear()
                await TonWalletUtils.clearToken()
                setShowSnackbar(true)
                Sentry.captureException(error)
            } finally {
                setIsLoading(false);
            }
        };

        loadWallet();
    }, [walletMnemonic]);

    return (
        <RecoveryPhraseContainer>
            <RecoveryDescription>
                {t('page.createWallet.steps.recoveryPhrase.description')}
            </RecoveryDescription>

            <Snackbar 
                message={t('errors.2001.description')} 
                isVisible={showSnackbar}
                onClose={() => setShowSnackbar(false)}
                duration={3000}
                width='90%'
                textAlign="center"
            />

            <RecoveryPhraseTitle>{t('page.createWallet.steps.recoveryPhrase.recoveryPhraseTitle')}</RecoveryPhraseTitle>

            {isLoading ? (
                <LoadingModal visible={isLoading} message={t('page.createWallet.steps.recoveryPhrase.loading')} />
            ) : (
                <RecoveryPhrase
                    mode="display"
                    phrases={phrases}
                />
            )}

            <ButtonWrapper>
                <PrimaryButton onClick={onNext} disabled={isLoading}>
                    {t('page.createWallet.steps.recoveryPhrase.continue')}
                </PrimaryButton>
            </ButtonWrapper>
        </RecoveryPhraseContainer>
    )
}

interface RecoveryPhraseStepProps extends WizardStepProps {
    walletMnemonic: string;
}

const RecoveryPhraseStep: React.FC<RecoveryPhraseStepProps> = ({ onNext, walletMnemonic }) => {
    const [showRecoveryPhrase, setShowRecoveryPhrase] = useState(false);

    if (showRecoveryPhrase) {
        return <RecoveryPhraseDisplay onNext={onNext} walletMnemonic={walletMnemonic} />;
    }

    return <ImportantNotes onContinue={() => setShowRecoveryPhrase(true)} />;
}

export default RecoveryPhraseStep;